import authRequest from 'utils/authRequest'
import studentRequest from 'utils/studentRequest'

// Base URLs come from the shared utils/*Request axios instances. Reading the env
// directly in THIS module is unsafe in this webpack build:
//   const { X } = process.env  -> leaves a dangling env statement -> "process is
//                                 not defined" blank screen at app load
//   member access process.env.X -> not inlined -> undefined -> DEV-URL fallback leak
// Reusing the shared instances (webpack inlines their base URLs cleanly) avoids both.
//   ums -> authRequest    (api-auth / UMS)  : public OTP endpoints
//   std -> studentRequest (api-std)         : also attaches the invite scoped JWT
//          from sessionStorage that the resolve call needs.
const ums = authRequest
const std = studentRequest

export const resolveInvite = (inviteToken) =>
  std.post('/assessment/invite/resolve', { inviteToken })

export const requestOtp = (inviteToken, email) =>
  ums.post('/public/assessment-otp/request', { inviteToken, email })

export const verifyOtp = (inviteToken, email, otp) =>
  ums.post('/public/assessment-otp/verify', { inviteToken, email, otp })

export const resendOtp = (inviteToken, email) =>
  ums.post('/public/assessment-otp/resend', { inviteToken, email })

// Generic invite-session keys. Used by every assessment-type invite flow
// (Aptitude / Communication / Hinglish / Custom / Behavior / AI_Interview).
export const INVITE_SESSION_KEYS = {
  scopedJwt: 'assessment_invite_scoped_jwt',
  assessmentAssignedId: 'assessment_invite_assigned_id',
  assessmentSetId: 'assessment_invite_set_id',
  assessmentType: 'assessment_invite_type',
  name: 'assessment_invite_name',
  email: 'assessment_invite_email',
  role: 'assessment_invite_role',
  company: 'assessment_invite_company',
  allowProctoring: 'assessment_invite_allow_proctoring',
  allowVerification: 'assessment_invite_allow_verification',
  studentId: 'assessment_invite_student_id',
  endTime: 'assessment_invite_end_time',
}

// Legacy keys written by the original AI-interview-only OTP flow. Kept so any
// in-flight AI-interview session (started before this generalization) keeps
// working.
export const LEGACY_INVITE_SESSION_KEYS = {
  scopedJwt: 'ai_interview_scoped_jwt',
  assessmentAssignedId: 'ai_interview_assessment_assigned_id',
}

// Read the scoped JWT preferring the new generic key, falling back to legacy.
export const readInviteScopedJwt = () => {
  if (typeof sessionStorage === 'undefined') return null
  return (
    sessionStorage.getItem(INVITE_SESSION_KEYS.scopedJwt) ||
    sessionStorage.getItem(LEGACY_INVITE_SESSION_KEYS.scopedJwt) ||
    null
  )
}

// Per-assignment "this AI interview is finished" flag. Persisted in localStorage
// (not sessionStorage) so it survives a full page reload and tab close — once a
// candidate completes the interview we must never drop them back onto the
// instructions/welcome screen, where they'd see a (correctly) 409'd retake.
export const aiInterviewCompletedKey = (assignedId) =>
  `ai_interview_completed_${assignedId}`

export const markAiInterviewCompleted = (assignedId) => {
  if (!assignedId || typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(aiInterviewCompletedKey(assignedId), '1')
  } catch (_) {
    /* storage disabled / quota — non-fatal, reload guard simply won't trigger */
  }
}

export const isAiInterviewCompleted = (assignedId) => {
  if (!assignedId || typeof localStorage === 'undefined') return false
  try {
    return localStorage.getItem(aiInterviewCompletedKey(assignedId)) === '1'
  } catch (_) {
    return false
  }
}

// Drop the flag when the server says this assignment is takeable again. An admin
// "resend invite" resets the SAME assessment_assigned_id back to PENDING, so this
// flag is a cache that can legitimately go stale — without this it was write-only
// and permanently locked resent candidates out of their retake.
export const clearAiInterviewCompleted = (assignedId) => {
  if (!assignedId || typeof localStorage === 'undefined') return
  try {
    localStorage.removeItem(aiInterviewCompletedKey(assignedId))
  } catch (_) {
    /* storage disabled — nothing cached, so nothing to clear */
  }
}

// Read the assessment-assigned id preferring the new generic key, then legacy.
export const readInviteAssignedId = () => {
  if (typeof sessionStorage === 'undefined') return null
  return (
    sessionStorage.getItem(INVITE_SESSION_KEYS.assessmentAssignedId) ||
    sessionStorage.getItem(LEGACY_INVITE_SESSION_KEYS.assessmentAssignedId) ||
    null
  )
}
